import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  HydraulicManufacturerForm,
  HydraulicManufacturerListMeta,
  HydraulicManufacturerRow,
  HydraulicManufacturerUpdate,
} from "../types";
import { initialForm, initialState } from "./initialState";

export const sliceKey = "hydraulicManufacturerState";

type HydraulicManufacturerListSuccessPayload = {
  data: HydraulicManufacturerRow[];
  meta: HydraulicManufacturerListMeta;
};

const slice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getListSuccess(
      state,
      action: PayloadAction<HydraulicManufacturerListSuccessPayload>,
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<HydraulicManufacturerListSuccessPayload>,
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof HydraulicManufacturerForm;
        value: string;
      }>,
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialForm;
    },

    add(state) {
      state.saving = true;
      state.error = null;
    },

    addSuccess(state, action: PayloadAction<HydraulicManufacturerRow>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialForm;
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    update(state, _action: PayloadAction<HydraulicManufacturerUpdate>) {
      state.saving = true;
      state.error = null;
    },

    updateSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    getDetail(state, _action: PayloadAction<string>) {
      state.detailLoading = true;
      state.detailError = null;
      state.detail = null;
    },

    getDetailSuccess(state, action: PayloadAction<HydraulicManufacturerRow>) {
      state.detailLoading = false;
      state.detail = action.payload;
      state.detailError = null;
    },

    getDetailError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },

    clearDetail(state) {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    },

    delete(state, _action: PayloadAction<string>) {
      state.saving = true;
      state.error = null;
    },

    deleteSuccess(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.hydraulicWallManufacturerId !== action.payload,
      );
      if (state.detail?.hydraulicWallManufacturerId === action.payload) {
        state.detail = null;
        state.detailLoading = false;
        state.detailError = null;
      }
    },

    deleteError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions, reducer } = slice;
